import { useContext, useEffect, useState,useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LocaleContext } from "../../contexts/LocaleContext";
import { cityService } from "../../services/cityService";
import { countryService } from "../../services/countryService";
import { categoryService } from "../../services/categoryService";
import { userService } from "../../services/userService";
import AddonsForm from "./components/AddonsForm";
import PackageForm from "./components/PackageForm";
import TripDestinationsForm from "./components/TripDestinationsForm";
import { set, useForm } from "react-hook-form";
import { tripService } from "../../services/tripService";

import { yupResolver } from "@hookform/resolvers/yup";
import { createTripSchema } from "../../validation/tripSchema";
import { toast } from "../../services/toastService";
import { addonCategoryService } from "../../services/addonCategoryService";
import { useValidation } from "../../hooks/useValidation";
import Loading from "../../components/common/Loading";
import BackButton from "../../components/common/BackButton";
import { urlConfig } from "../../config/baseUrl";
import LocationPicker from "../../components/LocationPicker";
import CouponForm from "./components/CouponForm";

const TripForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, lang } = useContext(LocaleContext);
  const [cities, setCities] = useState(null);
  const [countries, setCountries] = useState(null);
  const [categories, setCategories] = useState(null);
  const [providers, setProviders] = useState(null);
  const [addonsCategories, setAddonsCategories] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
   const [selectedImagePreviews, setSelectedImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const isEditMode = Boolean(id);

  const initialPackage = {
    nameEn: "",
    nameAr: "",
    offer: "", //percentage of offer for future use to get discount(1 to 100)
    isActive: true,
    cost: "",
    unit: "", //0=days 1=hours
    unitNumber: "", //number of days or hours
    description: "",
    descriptionEn: "",
    isSpecial: false,
    startDate: "", // if the package is special e.g. festival or even we should add the start date for this package
    endDate: "", // if the package is special e.g. festival or even we should add the end date for this package
  };
    const initialCoupon = {
    title: "",
    titleEn: "",
    body: "",
    bodyEn: "",
    code: "",
    discountValue: "",
    discountType: "",
    startDate: "",
    expirationDate: "",
    maxUseCount: "",
  };
    const workingDaysOptions = [
    { value: 0, label: t("saturday") },
    { value: 1, label: t("sunday") },
    { value: 2, label: t("monday") },
    { value: 3, label: t("tuesday") },
    { value: 4, label: t("wednesday") },
    { value: 5, label: t("thursday") },
    { value: 6, label: t("friday") },
  ];
  const initialForm = {
    title: "",
    titleEn: "",
    maxPersons: "",
    cityId: "",
    isInternational: false,
    tripDestinations: [{ countryId: "", cityId: "", order: 1, days: "" }],
    categoryId: "",
    latitude: "",
    longitude: "",
    providerId: "",
    availableFrom: "",
    availableTo: "",
    description: "",
    descriptionEn: "",
    order: "",
    isFeatured: false,
    //images array
    tripPhotos: [],
    //Addons
    addons: [],
    packages: [initialPackage],
    coupons: [initialCoupon],
    couponEnabled: false,
    workingDays: [],
  };

  useEffect(() => {
    loadData();
  }, []);
  const loadData = async () => {
    try {
      await setLoading(true);
      const countryList = await getAllCountries();
      await getAllCities(countryList);
      await getAllCategories();
      await getAllProviders();
      await getAllAddonsCategories();
      if (isEditMode) await editAsync();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const editAsync = async () => {
    const response = await tripService.edit(id);

    if (!response.isSuccess) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      return;
    }
    const data = response.value;
    setPreviewImages(data.previewImages);

    // tripDestinations only carries cityId (no countryId), so the country->city
    // cascading selects can't pre-populate on edit unless we resolve each
    // destination's countryId from the city record first.
    const resolvedDestinations =
      data.tripDestinations?.length > 0
        ? await Promise.all(
            data.tripDestinations.map(async (dest) => {
              try {
                const cityResponse = await cityService.edit(dest.cityId);
                return {
                  ...dest,
                  countryId: cityResponse?.value?.countryId ?? "",
                };
              } catch (error) {
                console.error(error);
                return { ...dest, countryId: "" };
              }
            }),
          )
        : [{ countryId: "", cityId: "", order: 1, days: "" }];

    reset({
      title: data.title,
      titleEn: data.titleEn,
      maxPersons: data.maxPersons,
      cityId: data.cityId,
      isInternational: data.isInternational ?? false,
      tripDestinations: resolvedDestinations,
      categoryId: data.categoryId,
      latitude: data.latitude,
      longitude: data.longitude,
      providerId: data.providerId,
      availableFrom: data.availableFrom,
      availableTo: data.availableTo,
      description: data.description,
      descriptionEn: data.descriptionEn,
      order: data.order,
      isFeatured: data.isFeatured,
      tripPhotos: [],
      addons: data.addons,
      packages: data.packages.map((p) => ({
        ...p,
        startDate: p.startDate ?? "",
        endDate: p.endDate ?? "",
      })),
       coupons: (data.coupons ?? []).map((c) => ({
        ...c,
        discountType:
          c.discountType === "Percentage"
            ? 0
            : c.discountType === "Fixed"
              ? 1
              : "",
        maxUseCount: c.maxUseCount ?? "",
        startDate: c.startDate ? c.startDate.split("T")[0] : "",
        expirationDate: c.expirationDate ? c.expirationDate.split("T")[0] : "",
      })),
      couponEnabled: (data.coupons ?? []).length > 0,
      workingDays: data.workingDays ?? [],
    });
  };
  // Domestic trips are Saudi-only, so the single-city dropdown must be
  // scoped to Saudi Arabia's cities, not every city across every country.
  const getAllCities = async (countryList) => {
    const saudi = countryList?.find(
      (c) => c.name?.en?.toLowerCase() === "saudi arabia",
    );
    if (!saudi) {
      setCities([]);
      return;
    }
    const data = await cityService.getDropdownByCountryAsync(saudi.id);
    const list = Array.isArray(data) ? data : data?.value ?? [];
    setCities(list);
  };
  const getAllCountries = async () => {
    var data = await countryService.getDropdownAsync();
    setCountries(data);
    return data;
  };
  const getAllCategories = async () => {
    var data = await categoryService.getDropdownAsync();
    setCategories(data);
  };
  const getAllProviders = async () => {
    var data = await userService.getDropdownAsync();
    setProviders(data);
  };
  const getAllAddonsCategories = async () => {
    var data = await addonCategoryService.getDropdownAsync();
    setAddonsCategories(data);
  };
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (selectedFiles.length === 0) return;

    const currentFiles = watch("tripPhotos") || [];

    const newFiles = [...currentFiles, ...selectedFiles];

    setValue("tripPhotos", newFiles, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    });

    const newPreviews = selectedFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    setSelectedImagePreviews((prev) => [...prev, ...newPreviews]);

    // Allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const removeSelectedImage = (index) => {
    const currentFiles = watch("tripPhotos") || [];

    const updatedFiles = currentFiles.filter((_, i) => i !== index);

    const imageToRemove = selectedImagePreviews[index];

    if (imageToRemove?.url) {
      URL.revokeObjectURL(imageToRemove.url);
    }

    const updatedPreviews = selectedImagePreviews.filter((_, i) => i !== index);

    setValue("tripPhotos", updatedFiles, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    });

    setSelectedImagePreviews(updatedPreviews);
  };
   const handleClear = () => {
    selectedImagePreviews.forEach((image) => {
      if (image.url) {
        URL.revokeObjectURL(image.url);
      }
    });

    setSelectedImagePreviews([]);
    reset(initialForm);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  //create validation schema

  const v = useValidation();
  const tripSchema = createTripSchema(v, t, isEditMode);
  //validation
  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: initialForm,
    resolver: yupResolver(tripSchema),
  });
   const tripPhotosRegister = register("tripPhotos");
  //submit form
  const onSubmit = async (values) => {
    try {
      setBtnLoading(true);
      const formData = new FormData();
      // cityId is always required (the trip's primary/listing city). For international trips,
      // it mirrors the first destination (order 1) since that's the trip's home/starting city.
      const primaryCityId = values.isInternational
        ? values.tripDestinations?.[0]?.cityId
        : values.cityId;

      // 1. Append simple fields
      Object.keys(values).forEach((key) => {
        // Skip files and arrays, we handle them specifically below
        if (!["tripPhotos", "packages", "addons", "tripDestinations", "cityId", "coupons", "couponEnabled", "workingDays"].includes(key)) {
          formData.append(key, values[key]);
        }
      });
      formData.append("cityId", primaryCityId ?? "");

      //working days

      if (values.workingDays?.length) {
        values.workingDays.forEach((day) => {
          formData.append("WorkingDays", day);
        });
      }

      //coupons
      if (values.couponEnabled && values.coupons?.length) {
        values.coupons.forEach((coupon, index) => {
          Object.keys(coupon).forEach((prop) => {
            const value = coupon[prop];

            if (value !== undefined && value !== null && value !== "") {
              formData.append(`Coupons[${index}].${prop}`, value);
            }
          });
        });
      }

      // 2. Append Files (must match the backend parameter name "files")
      values.tripPhotos.forEach((file) => formData.append("files", file));

      // 3. Append Complex Arrays (Packages & AddOns)
      // Backend expects: Packages[0].Title, Packages[0].Price, etc.
      values.packages?.forEach((pkg, index) => {
        Object.keys(pkg).forEach((prop) => {
          formData.append(`Packages[${index}].${prop}`, pkg[prop]);
        });
      });

      values.addons?.forEach((addon, index) => {
        Object.keys(addon).forEach((prop) => {
          formData.append(`AddOns[${index}].${prop}`, addon[prop]);
        });
      });

      // International trips: send the ordered destinations (countryId is UI-only, not sent)
      if (values.isInternational) {
        values.tripDestinations?.forEach((dest, index) => {
          formData.append(`tripDestinations[${index}].cityId`, dest.cityId);
          formData.append(`tripDestinations[${index}].order`, dest.order);
          formData.append(`tripDestinations[${index}].days`, dest.days);
        });
      }
      var response = null;
      if (isEditMode) {
        console.log(values);
        response = await tripService.update(id, formData);
        console.log(response);
        toast.success(t("recordUpdatedSuccessfully"));
        navigate("/trips");
      } else {
        response = await tripService.create(formData);
        toast.success(t("recordAddedSuccessfully"));
      }

      handleClear();
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };
  const deleteImage = async (id) => {
    try {
      setLoading(true);
      var response = await tripService.deleteImage(id);
      if (!response.isSuccess) {
        toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        return;
      } else {
        toast.success(t("recordDeletedSuccessfully"));
        setPreviewImages(previewImages.filter((i) => i.id !== id));
      }
    } catch (error) {
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  if (loading) return <Loading />;
  return (
    <section className="section-trip-form">
      <div className="default-heading">{t("tripsDashboardAndManagement")}</div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-card">
          <div className="my-card-header d-flex justify-content-between align-items-center">
            {isEditMode ? (
              <>
                <i className="fas fa-edit"></i> {t("editOrSelectedNarrator")}
              </>
            ) : (
              <>
                <i className="fas fa-plus plus-icon"></i> {t("addNewTrip")}
              </>
            )}
            <BackButton />
          </div>
          <div className="my-card">
            <div className="my-card-header text-primary">
              {t("basicTripInformation")}
            </div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("tripTitle")}</label>
                    <input
                      type="text"
                      {...register("title")}
                      className="form-control"
                      placeholder={t("tripTitle")}
                    />
                    {errors.title && (
                      <p className="text-danger">{errors.title.message}</p>
                    )}
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("titleInEnglish")}</label>
                    <input
                      type="text"
                      {...register("titleEn")}
                      className="form-control"
                      placeholder={t("titleInEnglish")}
                    />
                    {errors.titleEn && (
                      <p className="text-danger">{errors.titleEn.message}</p>
                    )}
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("maxNoOfPeople")}</label>
                    <input
                      type="number"
                      {...register("maxPersons")}
                      className="form-control"
                      placeholder={t("maxNoOfPeople")}
                    />
                    {errors.maxPersons && (
                      <p className="text-danger">{errors.maxPersons.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("locationAndCategory")}
            </div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12">
                  <div className="form-group d-flex gap-1 align-items-center">
                    <input type="checkbox" {...register("isInternational")} />
                    <label>{t("isInternationalTrip")}</label>
                  </div>
                </div>
                {!watch("isInternational") && (
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">{t("city")}</label>
                      <select className="form-control" {...register("cityId")}>
                        <option>--{t("city")}--</option>
                        {cities &&
                          cities.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name[lang]}
                            </option>
                          ))}
                      </select>
                      {errors.cityId && (
                        <p className="text-danger">{errors.cityId.message}</p>
                      )}
                    </div>
                  </div>
                )}
                <div
                  className={
                    watch("isInternational") ? "col-12" : "col-12 col-md-6"
                  }
                >
                  <div className="form-group">
                    <label className="form-label">{t("category")}</label>
                    <select
                      className="form-control"
                      {...register("categoryId")}
                    >
                      <option>--{t("category")}--</option>
                      {categories &&
                        categories.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name[lang]}
                          </option>
                        ))}
                    </select>
                    {errors.categoryId && (
                      <p className="text-danger">{errors.categoryId.message}</p>
                    )}
                  </div>
                </div>
                {watch("isInternational") && (
                  <div className="col-12 mb-4">
                    <TripDestinationsForm
                      t={t}
                      control={control}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      countries={countries}
                      lang={lang}
                    />
                  </div>
                )}
                <div className="col-12">
                  <div className="form-group">
                    <LocationPicker
                      setValue={setValue}
                      latitude={watch("latitude")}
                      longitude={watch("longitude")}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t("latitude")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("latitude")}
                      {...register("latitude")}
                    />
                    {errors.latitude && (
                      <p className="text-danger">{errors.latitude.message}</p>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t("longitude")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("longitude")}
                      {...register("longitude")}
                    />
                    {errors.longitude && (
                      <p className="text-danger">{errors.longitude.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("serviceProvider")}
            </div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">
                      {t("selectServiceProvider")}
                    </label>
                    <select
                      className="form-control"
                      {...register("providerId")}
                    >
                      <option>--{t("selectServiceProvider")}--</option>
                      {providers &&
                        providers.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </select>
                    {errors.providerId && (
                      <p className="text-danger">{errors.providerId.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("availabilityTimes")}
            </div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t("availableFrom")}</label>
                    <input
                      type="time"
                      {...register("availableFrom")}
                      className="form-control"
                      placeholder={t("availableFrom")}
                    />
                  </div>
                  {errors.availableFrom && (
                    <p className="text-danger">
                      {errors.availableFrom.message}
                    </p>
                  )}
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t("availableUntil")}</label>
                    <input
                      type="time"
                      {...register("availableTo")}
                      className="form-control"
                      placeholder={t("availableUntil")}
                    />
                    {errors.availableTo && (
                      <p className="text-danger">
                        {errors.availableTo.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("tripDescription")}
            </div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">
                      {t("discriptionInArabic")}
                    </label>
                    <textarea
                      type="text"
                      {...register("description")}
                      className="form-control"
                      placeholder={t("discriptionInArabic")}
                      rows="4"
                    />
                    {errors.description && (
                      <p className="text-danger">
                        {errors.description.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-group">
                    <label className="form-label">
                      {t("descriptionInEnglish")}
                    </label>
                    <textarea
                      type="text"
                      {...register("descriptionEn")}
                      className="form-control"
                      placeholder={t("descriptionInEnglish")}
                      rows="4"
                    />
                    {errors.descriptionEn && (
                      <p className="text-danger">
                        {errors.descriptionEn.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">
              {t("workingDays")}
            </div>

            <div className="my-card-body">
              <div className="row">
                {workingDaysOptions.map((day) => (
                  <div className="col-12 col-md-6 col-lg-4" key={day.value}>
                    <div className="form-group d-flex gap-1 align-items-center">
                      <input
                        type="checkbox"
                        value={day.value}
                        {...register("workingDays")}
                        defaultChecked={watch("workingDays")?.includes(
                          day.value,
                        )}
                      />
                      <label>{day.label}</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">{t("tripPhotos")}</div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("tripPhotos")}</label>
                    <input
                      type="file"
                      id="trip-photos"
                      accept="image/*"
                      multiple
                      {...tripPhotosRegister}
                      onChange={handleFileChange}
                      ref={(element) => {
                        tripPhotosRegister.ref(element);
                        fileInputRef.current = element;
                      }}
                      className="form-control d-none"
                    />
                    <label htmlFor="trip-photos" className="my-file-upload">
                      <label htmlFor="trip-photos" className="cursor-pointer">
                        {t("dragAndDropTheImageHereOr")}{" "}
                        <span className="text-primary">
                          {t("browseImages")}{" "}
                          {watch("tripPhotos")?.length > 0
                            ? `(${watch("tripPhotos")?.length})`
                            : ""}
                        </span>
                      </label>
                      <i className="fas fa-camera" />
                    </label>
                    {errors.tripPhotos && (
                      <p className="text-danger">{errors.tripPhotos.message}</p>
                    )}
                  </div>
                </div>
                {previewImages && (
                  <div className="col-12">
                    <div className="preview-images">
                      {previewImages?.map((item) => (
                        <div className="preview-image" key={item.id}>
                          <img
                            src={`${urlConfig.imageBaseUrl}/${item.imagePath}`}
                          />
                          <i
                            className="fas fa-times"
                            onClick={() => deleteImage(item.id)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedImagePreviews.length > 0 && (
                  <div className="col-12">
                    <div className="preview-images">
                      {selectedImagePreviews.map((item, index) => (
                        <div className="preview-image" key={item.url}>
                          <img src={item.url} alt={`Preview ${index + 1}`} />

                          <i
                            className="fas fa-times"
                            onClick={() => removeSelectedImage(index)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="my-card mt-3">
            <div className="my-card-header text-primary">{t("settings")}</div>
            <div className="my-card-body">
              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">{t("displayOrder")}</label>
                    <input
                      type="number"
                      {...register("order")}
                      className="form-control"
                      placeholder={t("displayOrder")}
                    />
                    {errors.order && (
                      <p className="text-danger">{errors.order.message}</p>
                    )}
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group d-flex gap-1 align-items-center">
                    <input type="checkbox" {...register("isFeatured")} />
                    <label>{t("featuredTrip")}</label>
                    {errors.isFeatured && (
                      <p className="text-danger">{errors.isFeatured.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AddonsForm
            t={t}
            control={control}
            register={register}
            errors={errors}
            addonCategories={addonsCategories}
            lang={lang}
          />
          <PackageForm
            t={t}
            control={control}
            register={register}
            errors={errors}
          />
          <CouponForm
            t={t}
            register={register}
            errors={errors}
            control={control}
            watch={watch}
          />
          <div className="mt-3 gap-2 d-flex justify-content-end">
            {!btnLoading && (
              <button type="submit" className="btn-default">
                {t("save")}
              </button>
            )}
            {btnLoading && (
              <button type="button" className="btn-default">
                <span
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </button>
            )}
            {isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {t("reset")}
              </button>
            )}
            {!isEditMode && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClear}
              >
                {t("reset")}
              </button>
            )}
          </div>
        </div>
      </form>
    </section>
  );
};

export default TripForm;
